<script lang="ts">
  /**
   * AbsenceChart — Gráfico de barras agrupadas de faltas e jornadas curtas
   * normalizadas como % de colaboradores afetados por dia.
   *
   * Escala única (eixo Y em %) — elimina a confusão de dois eixos com
   * valores absolutos em escalas diferentes.
   *
   * Design:
   *  - Barras roxas: % faltas (ausência total em dia de escala)
   *  - Barras laranja: % jornadas curtas (atraso / saída antecipada > tolerância)
   *  - Tooltip: mostra % + contagem absoluta
   *  - KPIs no topo: pico %, média %, totais absolutos
   *
   * Privacidade: recebe apenas dados já agregados — sem PII.
   */
  import type { DailyAbsencePoint, AbsenceChartKpis } from '$core/metrics/dailyAbsenceAnalytics';

  export let points: DailyAbsencePoint[] = [];
  export let kpis: AbsenceChartKpis;

  // =========================================================================
  // Dimensões do SVG (responsivo via viewBox)
  // =========================================================================
  const MARGIN = { top: 16, right: 16, bottom: 48, left: 40 };
  const SVG_WIDTH = 800;
  const SVG_HEIGHT = 260;

  $: innerWidth = SVG_WIDTH - MARGIN.left - MARGIN.right;
  $: innerHeight = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;

  // =========================================================================
  // Escala Y
  // =========================================================================

  /** Máximo de % em qualquer série (+ buffer de 5pp ou 10%, o que for maior). */
  $: rawMax = Math.max(
    ...points.map(p => Math.max(p.absencePct, p.shortJourneyPct)),
    0,
  );

  /** Ceil ao próximo múltiplo de 10 para o gridline limpo. */
  $: yMax = rawMax === 0 ? 10 : Math.ceil((rawMax * 1.2) / 10) * 10;
  $: yMax100 = Math.min(yMax, 100); // nunca exibir acima de 100%

  /** Gridlines a cada 10pp (se yMax <= 50) ou a cada 25pp (se yMax > 50). */
  $: gridStep = yMax100 <= 50 ? 10 : 25;
  $: gridLines = Array.from(
    { length: Math.floor(yMax100 / gridStep) + 1 },
    (_, i) => i * gridStep,
  );

  function yPos(pct: number): number {
    return innerHeight - (pct / yMax100) * innerHeight;
  }

  // =========================================================================
  // Escala X — barras agrupadas
  // =========================================================================
  const GROUP_GAP = 0.25; // proporção do grupo reservada para espaço
  const BAR_GAP = 0.1;    // espaço entre as duas barras dentro do grupo

  $: n = points.length;
  $: groupWidth = n > 0 ? innerWidth / n : 0;
  $: barWidth = n > 0 ? (groupWidth * (1 - GROUP_GAP) - BAR_GAP) / 2 : 0;

  function groupX(i: number): number {
    return i * groupWidth + (groupWidth * GROUP_GAP) / 2;
  }

  function bar1X(i: number): number { return groupX(i); }
  function bar2X(i: number): number { return groupX(i) + barWidth + BAR_GAP; }

  // =========================================================================
  // Formatação de data para o eixo X
  // =========================================================================

  /**
   * Mostra só rótulos a cada N dias para não sobrecarregar o eixo X.
   * Para até 14 dias: todos; até 31: a cada 3; mais de 31: a cada 7.
   */
  $: labelEvery = n <= 14 ? 1 : n <= 31 ? 3 : 7;

  function formatDateLabel(iso: string): string {
    const [, m, d] = iso.split('-');
    return `${d}/${m}`;
  }

  // =========================================================================
  // Tooltip
  // =========================================================================
  let tooltipVisible = false;
  let tooltipPoint: DailyAbsencePoint | null = null;
  let tooltipX = 0;
  let tooltipY = 0;

  function showTooltip(event: MouseEvent | FocusEvent, point: DailyAbsencePoint) {
    tooltipVisible = true;
    tooltipPoint = point;
    const rect = (event.target as SVGElement).getBoundingClientRect();
    tooltipX = rect.left + rect.width / 2;
    tooltipY = rect.top;
  }

  function hideTooltip() {
    tooltipVisible = false;
    tooltipPoint = null;
  }

  // =========================================================================
  // Formatação de números
  // =========================================================================

  function fmtPct(v: number): string {
    return `${v.toFixed(1).replace('.', ',')}%`;
  }

  function fmtDate(iso: string): string {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  }
</script>

<div class="absence-chart">
  <!-- KPIs no topo -->
  <div class="absence-chart__kpis">
    <div class="absence-chart__kpi">
      <span class="absence-chart__kpi-label">TOTAL DE FALTAS</span>
      <span class="absence-chart__kpi-value absence-chart__kpi-value--purple">
        {kpis.totalAbsences}
      </span>
    </div>
    <div class="absence-chart__kpi">
      <span class="absence-chart__kpi-label">TOTAL JORNADAS CURTAS</span>
      <span class="absence-chart__kpi-value absence-chart__kpi-value--orange">
        {kpis.totalShortJourneys}
      </span>
    </div>
    <div class="absence-chart__kpi">
      <span class="absence-chart__kpi-label">PICO DE FALTAS</span>
      <span class="absence-chart__kpi-value absence-chart__kpi-value--purple">
        {fmtPct(kpis.peakAbsencePct)}
        {#if kpis.peakAbsenceDate}
          <span class="absence-chart__kpi-sub">em {fmtDate(kpis.peakAbsenceDate)} ({kpis.peakAbsenceCount} colab.)</span>
        {/if}
      </span>
    </div>
    <div class="absence-chart__kpi">
      <span class="absence-chart__kpi-label">MÉDIA DIÁRIA DE FALTAS</span>
      <span class="absence-chart__kpi-value absence-chart__kpi-value--purple">
        {fmtPct(kpis.avgAbsencePct)}
      </span>
    </div>
  </div>

  <!-- Legenda -->
  <div class="absence-chart__legend" aria-label="Legenda do gráfico">
    <span class="absence-chart__legend-item">
      <span class="absence-chart__legend-dot absence-chart__legend-dot--purple" aria-hidden="true"></span>
      Faltas (% de colaboradores)
    </span>
    <span class="absence-chart__legend-item">
      <span class="absence-chart__legend-dot absence-chart__legend-dot--orange" aria-hidden="true"></span>
      Jornadas curtas (% de colaboradores)
    </span>
  </div>

  <!-- Gráfico SVG -->
  <div class="absence-chart__svg-wrapper" role="img" aria-label="Gráfico de barras agrupadas: faltas e jornadas curtas por dia como % de colaboradores afetados">
    {#if points.length === 0}
      <p class="absence-chart__empty">Sem dados de jornada disponíveis para gerar o gráfico.</p>
    {:else}
      <svg
        viewBox="0 0 {SVG_WIDTH} {SVG_HEIGHT}"
        class="absence-chart__svg"
        preserveAspectRatio="xMidYMid meet"
      >
        <g transform="translate({MARGIN.left}, {MARGIN.top})">

          <!-- Gridlines -->
          {#each gridLines as gridVal (gridVal)}
            {@const gy = yPos(gridVal)}
            <line
              x1="0" y1={gy}
              x2={innerWidth} y2={gy}
              class="absence-chart__gridline"
            />
            <text
              x="-6" y={gy}
              class="absence-chart__axis-label absence-chart__axis-label--y"
              text-anchor="end"
              dominant-baseline="middle"
            >{gridVal}%</text>
          {/each}

          <!-- Barras -->
          {#each points as point, i (point.date)}
            <!-- Barra de faltas (roxa) -->
            {@const absH = Math.max((point.absencePct / yMax100) * innerHeight, 1)}
            <rect
              x={bar1X(i)}
              y={innerHeight - absH}
              width={Math.max(barWidth, 1)}
              height={absH}
              class="absence-chart__bar absence-chart__bar--absence"
              role="img"
              on:mouseenter={(e) => showTooltip(e, point)}
              on:mouseleave={hideTooltip}
              on:focusin={(e) => showTooltip(e, point)}
              on:focusout={hideTooltip}
              aria-label="Faltas em {formatDateLabel(point.date)}: {fmtPct(point.absencePct)} ({point.absenceCount} colaboradores)"
            />

            <!-- Barra de jornadas curtas (laranja) -->
            {@const shortH = Math.max((point.shortJourneyPct / yMax100) * innerHeight, 1)}
            <rect
              x={bar2X(i)}
              y={innerHeight - shortH}
              width={Math.max(barWidth, 1)}
              height={shortH}
              class="absence-chart__bar absence-chart__bar--short"
              role="img"
              on:mouseenter={(e) => showTooltip(e, point)}
              on:mouseleave={hideTooltip}
              on:focusin={(e) => showTooltip(e, point)}
              on:focusout={hideTooltip}
              aria-label="Jornadas curtas em {formatDateLabel(point.date)}: {fmtPct(point.shortJourneyPct)} ({point.shortJourneyCount} colaboradores)"
            />

            <!-- Label eixo X -->
            {#if i % labelEvery === 0}
              <text
                x={groupX(i) + groupWidth * (1 - GROUP_GAP) / 2}
                y={innerHeight + 14}
                class="absence-chart__axis-label absence-chart__axis-label--x"
                text-anchor="middle"
              >{formatDateLabel(point.date)}</text>
            {/if}
          {/each}

          <!-- Eixo X (linha base) -->
          <line
            x1="0" y1={innerHeight}
            x2={innerWidth} y2={innerHeight}
            class="absence-chart__axis-line"
          />

          <!-- Eixo Y (linha lateral) -->
          <line
            x1="0" y1="0"
            x2="0" y2={innerHeight}
            class="absence-chart__axis-line"
          />

        </g>
      </svg>
    {/if}
  </div>

  <!-- Tooltip (fora do SVG, posicionado via CSS/JS) -->
  {#if tooltipVisible && tooltipPoint !== null}
    {@const tp = tooltipPoint}
    <div
      class="absence-chart__tooltip"
      style="left: {tooltipX}px; top: {tooltipY}px;"
      role="tooltip"
    >
      <strong class="absence-chart__tooltip-date">{fmtDate(tp.date)}</strong>
      <div class="absence-chart__tooltip-row">
        <span class="absence-chart__tooltip-dot absence-chart__tooltip-dot--purple"></span>
        <span>Faltas: <strong>{fmtPct(tp.absencePct)}</strong> ({tp.absenceCount} colab.)</span>
      </div>
      <div class="absence-chart__tooltip-row">
        <span class="absence-chart__tooltip-dot absence-chart__tooltip-dot--orange"></span>
        <span>Jornadas curtas: <strong>{fmtPct(tp.shortJourneyPct)}</strong> ({tp.shortJourneyCount} colab.)</span>
      </div>
      <div class="absence-chart__tooltip-row absence-chart__tooltip-row--small">
        <span>Ativos no dia: {tp.activeEmployees}</span>
      </div>
    </div>
  {/if}
</div>

<style>
  .absence-chart {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-3);
    position: relative;
    font-family: var(--ptt-font-sans, sans-serif);
  }

  /* KPIs */
  .absence-chart__kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: var(--ptt-space-3);
  }

  .absence-chart__kpi {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-1);
    padding: var(--ptt-space-3) var(--ptt-space-4);
    background: var(--ptt-gray-10, #f5f5f5);
    border-radius: var(--ptt-radius-md);
  }

  .absence-chart__kpi-label {
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ptt-gray-200, #565656);
  }

  .absence-chart__kpi-value {
    font-size: 1.375rem;
    font-weight: 700;
    line-height: 1.1;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .absence-chart__kpi-value--purple {
    color: var(--ptt-purple-100, #7F3D7D);
  }

  .absence-chart__kpi-value--orange {
    color: var(--ptt-orange-100, #FB7224);
  }

  .absence-chart__kpi-sub {
    font-size: 0.75rem;
    font-weight: 400;
    color: var(--ptt-gray-200, #565656);
  }

  /* Legenda */
  .absence-chart__legend {
    display: flex;
    gap: var(--ptt-space-4);
    flex-wrap: wrap;
    font-size: 0.8125rem;
    color: var(--ptt-gray-200, #565656);
  }

  .absence-chart__legend-item {
    display: flex;
    align-items: center;
    gap: var(--ptt-space-2);
  }

  .absence-chart__legend-dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .absence-chart__legend-dot--purple {
    background: var(--ptt-purple-100, #7F3D7D);
  }

  .absence-chart__legend-dot--orange {
    background: var(--ptt-orange-100, #FB7224);
  }

  /* SVG wrapper */
  .absence-chart__svg-wrapper {
    width: 100%;
    overflow: hidden;
  }

  .absence-chart__svg {
    width: 100%;
    height: auto;
    display: block;
  }

  .absence-chart__empty {
    font-size: 0.875rem;
    color: var(--ptt-gray-100, #7d7d7d);
    font-style: italic;
    text-align: center;
    padding: var(--ptt-space-6) 0;
    margin: 0;
  }

  /* SVG elements via :global (inside svg) */
  :global(.absence-chart__gridline) {
    stroke: #e7e7e7;
    stroke-width: 1;
    stroke-dasharray: 3 3;
  }

  :global(.absence-chart__axis-line) {
    stroke: #7d7d7d;
    stroke-width: 1;
  }

  :global(.absence-chart__axis-label) {
    font-family: var(--ptt-font-sans, Arial, sans-serif);
    fill: #7d7d7d;
  }

  :global(.absence-chart__axis-label--y) {
    font-size: 10px;
  }

  :global(.absence-chart__axis-label--x) {
    font-size: 10px;
  }

  :global(.absence-chart__bar) {
    cursor: default;
    transition: opacity 0.1s ease;
    outline: none;
  }

  :global(.absence-chart__bar:hover),
  :global(.absence-chart__bar:focus-visible) {
    opacity: 0.75;
  }

  :global(.absence-chart__bar--absence) {
    fill: #7F3D7D;
  }

  :global(.absence-chart__bar--short) {
    fill: #FB7224;
  }

  /* Tooltip */
  .absence-chart__tooltip {
    position: fixed;
    transform: translate(-50%, calc(-100% - 8px));
    background: var(--ptt-gray-300, #323232);
    color: #fff;
    border-radius: var(--ptt-radius-md);
    padding: var(--ptt-space-3) var(--ptt-space-4);
    font-size: 0.8125rem;
    line-height: 1.5;
    pointer-events: none;
    z-index: 100;
    white-space: nowrap;
    box-shadow: var(--ptt-shadow-md);
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-1);
  }

  .absence-chart__tooltip-date {
    font-size: 0.875rem;
    border-bottom: 1px solid rgba(255,255,255,0.2);
    padding-bottom: var(--ptt-space-1);
    margin-bottom: 2px;
  }

  .absence-chart__tooltip-row {
    display: flex;
    align-items: center;
    gap: var(--ptt-space-2);
  }

  .absence-chart__tooltip-row--small {
    font-size: 0.75rem;
    opacity: 0.7;
    margin-top: 2px;
  }

  .absence-chart__tooltip-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .absence-chart__tooltip-dot--purple {
    background: #b06aae;
  }

  .absence-chart__tooltip-dot--orange {
    background: #fb9259;
  }

  /* Responsividade */
  @media (max-width: 768px) {
    .absence-chart__kpis {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 480px) {
    .absence-chart__kpis {
      grid-template-columns: 1fr 1fr;
    }

    .absence-chart__kpi-value {
      font-size: 1.125rem;
    }
  }
</style>
